/**
 * ResultsSection - Results Tab Content
 *
 * Extracts the optimization results display from ScenarioEditor Tab 6.
 * Contains multiple sub-tabs:
 * - Performance: System design, power flow, monthly performance
 * - Deal Analysis: Financial analysis and projections
 * - CA DER Charges: California DER charges (conditional)
 * - Export: Data export options
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Tab,
    Tabs,
    Button,
    Typography,
    CircularProgress,
    LinearProgress,
    FormControlLabel,
    Switch,
    IconButton,
} from '@mui/material';
import {
    Analytics,
    Build,
    ElectricBolt,
    ShowChart,
    Shield,
    TrendingUp,
    FileDownload,
    MonetizationOn,
    ViewInAr as ViewInArIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { alpha } from '@mui/material/styles';
import { SectionCard, SystemDesignSummary, PowerGraph, EmissionsPanel, ExportDataTab, VPPRevenueTab, vortxColors, vortxTypography, useAuth } from '@vortx/shared-ui';
import ResultsSummary from '../../../ResultsSummary';
import MonthlyPerformanceTable from '../../../MonthlyPerformanceTable';
import ResiliencePanel from '../../../ResiliencePanel';
import UnmetLoadTable from '../../../UnmetLoadTable';
import DealAnalysisTab from '../../../components/DealAnalysisTab';
import CADERChargesAnalysis from '../../../components/CADERChargesAnalysis';
import { OutageProbabilityChart, OutageHeatmap } from '../../../components/OutageVisualizations';

const DispatchRibbon3D = React.lazy(() =>
  import('../../../../../vortx-shared-ui/src/data-display/DispatchRibbon3D')
);

/**
 * Loading State Card
 */
function LoadingCard({ title, message }) {
    return (
        <SectionCard title={title} icon={<Analytics color="primary" />}>
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress
                    size={60}
                    sx={{ color: vortxColors.gold, mb: 3 }}
                />
                <Typography
                    variant="h5"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        fontFamily: vortxTypography.heading,
                        mb: 2
                    }}
                >
                    {message}
                </Typography>
            </Box>
        </SectionCard>
    );
}

/**
 * Optimization In Progress Card
 */
function OptimizationInProgressCard({ status, runUuid }) {
    return (
        <SectionCard title="Optimization In Progress" icon={<Analytics color="primary" />}>
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress
                    size={60}
                    sx={{ color: vortxColors.gold, mb: 3 }}
                />
                <Typography
                    variant="h5"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        fontFamily: vortxTypography.heading,
                        mb: 2
                    }}
                >
                    {status || 'Submitting...'}
                </Typography>
                <LinearProgress
                    sx={{
                        width: '60%',
                        mx: 'auto',
                        mb: 2,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(vortxColors.gold, 0.2),
                        '& .MuiLinearProgress-bar': {
                            bgcolor: vortxColors.gold,
                        }
                    }}
                />
                <Typography variant="body2" color="textSecondary">
                    This may take 1-3 minutes depending on model complexity.
                </Typography>
                {runUuid && (
                    <Typography variant="caption" sx={{ mt: 2, display: 'block', color: vortxColors.gray }}>
                        Run ID: {runUuid}
                    </Typography>
                )}
            </Box>
        </SectionCard>
    );
}

/**
 * No Results Card
 */
function NoResultsCard({ onNavigateToDesign, vortxStyles }) {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="textSecondary">
                No results yet. Run an optimization to see performance and financial analysis.
            </Typography>
            <Button
                variant="contained"
                sx={{ mt: 3, ...vortxStyles.button }}
                onClick={onNavigateToDesign}
            >
                Go to Microgrid Design
            </Button>
        </Box>
    );
}

/**
 * Performance Sub-tab Content
 */
function PerformanceSubTab({
    optimization,
    scenarioForm,
    loadProfile,
    financialAnalysis,
    capexSource,
    monthlyPerformanceData,
    optimizationEngine,
    vortxStyles,
    graphData,
    tph,
    isDark,
    vppEstimate,
    // Power flow state
    selectedDate,
    setSelectedDate,
    dayIndex,
    setDayIndex,
    showResilientMode,
    setShowResilientMode,
    outageStartHour,
    setOutageStartHour,
}) {
    const [show3D, setShow3D] = useState(false);

    return (
        <>
            <SectionCard title="System Design" icon={<Build color="primary" />}>
                <SystemDesignSummary results={optimization.results} />
            </SectionCard>

            <SectionCard title="Executive Summary" icon={<TrendingUp color="primary" />}>
                <ResultsSummary
                    results={optimization.results}
                    analysis={financialAnalysis}
                    capexSource={capexSource}
                    vppEstimate={vppEstimate}
                />
            </SectionCard>

            <SectionCard title="Power Flow" icon={<ShowChart color="primary" />}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DatePicker
                                label="Select Date"
                                views={["month", "day"]}
                                openTo="day"
                                format="MM/dd"
                                value={selectedDate}
                                minDate={loadProfile.startDate}
                                maxDate={new Date(loadProfile.startDate.getFullYear(), 11, 31)}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setSelectedDate(newValue);
                                        const diff = Math.round(
                                            (newValue - loadProfile.startDate) / (24 * 60 * 60 * 1000)
                                        );
                                        setDayIndex(String(Math.min(364, Math.max(0, diff))));
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            ...vortxStyles.textField,
                                            maxWidth: 150,
                                        }
                                    }
                                }}
                            />
                            <Typography variant="caption" sx={{ ml: 2, color: vortxColors.gray }}>
                                Day {parseInt(dayIndex, 10) + 1} of 365 • Peak load day auto-selected
                            </Typography>
                        </Box>

                        {optimizationEngine === 'vortx_flux' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {showResilientMode && (
                                    <Box sx={{ width: 150 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Outage Time: {outageStartHour}:00
                                        </Typography>
                                        <input
                                            type="range"
                                            min="0"
                                            max="23"
                                            value={outageStartHour}
                                            onChange={(e) => setOutageStartHour(parseInt(e.target.value, 10))}
                                            style={{ width: '100%', cursor: 'pointer' }}
                                        />
                                    </Box>
                                )}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showResilientMode}
                                            onChange={(e) => setShowResilientMode(e.target.checked)}
                                            color="error"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: showResilientMode ? 'error.main' : 'text.primary' }}>
                                            Resilient Operations
                                        </Typography>
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </LocalizationProvider>
                <Box display="flex" justifyContent="flex-end" mb={0.5}>
                    <IconButton
                        size="small"
                        onClick={() => setShow3D(prev => !prev)}
                        sx={{
                            color: show3D ? vortxColors.gold : vortxColors.gray,
                            border: show3D ? `1px solid ${vortxColors.gold}` : '1px solid transparent',
                            borderRadius: '6px',
                            px: 1,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontFamily: "'JetBrains Mono', monospace",
                            transition: 'all 0.2s ease',
                        }}
                        title={show3D ? 'Switch to 2D chart' : 'Switch to 3D view'}
                    >
                        <ViewInArIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                        3D
                    </IconButton>
                </Box>
                {show3D ? (
                    <React.Suspense fallback={<Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading 3D...</Typography></Box>}>
                        <Box sx={{ height: 400 }}>
                            <DispatchRibbon3D
                                data={graphData}
                                isDark={isDark}
                                isResilientMode={showResilientMode}
                            />
                        </Box>
                    </React.Suspense>
                ) : (
                    <PowerGraph
                        data={graphData}
                        dayIndex={parseInt(dayIndex, 10)}
                        onDayChange={setDayIndex}
                        tph={tph}
                        isDark={isDark}
                        isResilientMode={showResilientMode}
                    />
                )}
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 1, color: vortxColors.gray }}>
                    Modeled year: {loadProfile.loadYear}
                </Typography>
            </SectionCard>

            <SectionCard title="Monthly Performance Analysis" icon={<Analytics color="primary" />}>
                <MonthlyPerformanceTable
                    results={optimization.results}
                    runUuid={optimization.runUuid || optimization.lastRunUuid}
                    tariff={scenarioForm.customTariffData || scenarioForm.selectedTariffData}
                    caDerConfig={scenarioForm.caDerConfig}
                    monthlyPerformanceData={monthlyPerformanceData}
                />
            </SectionCard>

            {/* Vortx Flux Extended Results */}
            {optimizationEngine === 'vortx_flux' && (
                <>
                    <SectionCard title="Resilience" icon={<Shield color="primary" />}>
                        {optimization.results?.outputs?.Site?.survivability ? (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: vortxColors.gold }}>
                                    Outage Survival Analysis
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 3 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">P10 (Worst 10%)</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.Site.survivability.p10_hours || optimization.results.outputs.Site.survivability.min_hours} hrs</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Median (P50)</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.Site.survivability.p50_hours || optimization.results.outputs.Site.survivability.avg_hours} hrs</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">P90 (Best 90%)</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.Site.survivability.p90_hours || optimization.results.outputs.Site.survivability.max_hours} hrs</Typography>
                                    </Box>
                                    <Box sx={{ gridColumn: 'span 3', mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <span>Critical Load: <strong>{optimization.results.outputs.Site.survivability.critical_load_kw?.toLocaleString() || '--'} kW</strong></span>
                                            <span>Battery: <strong>{optimization.results.outputs.Site.survivability.battery_kwh?.toLocaleString() || '--'} kWh</strong></span>
                                            {optimization.results.outputs.Site.survivability.pv_kw > 0 && (
                                                <span>PV: <strong>{optimization.results.outputs.Site.survivability.pv_kw?.toLocaleString()} kW</strong></span>
                                            )}
                                            {optimization.results.outputs.Site.survivability.simulations_run > 0 && (
                                                <span style={{ opacity: 0.7 }}>({optimization.results.outputs.Site.survivability.simulations_run} simulations)</span>
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                                    {optimization.results.outputs.Site.survivability.survival_probability_curve && (
                                        <Box>
                                            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: 'text.primary' }}>
                                                Probability of Coverage
                                            </Typography>
                                            <OutageProbabilityChart
                                                data={optimization.results.outputs.Site.survivability.survival_probability_curve}
                                            />
                                        </Box>
                                    )}

                                    {optimization.results.outputs.Site.survivability.survival_hours_matrix && (
                                        <Box>
                                            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: 'text.primary' }}>
                                                Seasonal Coverage Heatmap
                                            </Typography>
                                            <OutageHeatmap
                                                survivalHours={optimization.results.outputs.Site.survivability.survival_hours_matrix}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No resilience constraints modeled.</Typography>
                        )}
                    </SectionCard>

                    <SectionCard title="Degradation & Performance Risk" icon={<ElectricBolt color="primary" />}>
                        {optimization.results?.outputs?.ElectricStorage?.degradation ? (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: vortxColors.gold }}>
                                    Battery Lifecycle
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">EOL Capacity</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.ElectricStorage.degradation.eol_capacity_pct}%</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Years to 80%</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.ElectricStorage.degradation.years_to_80_pct}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Annual Cycles</Typography>
                                        <Typography variant="body1">{optimization.results.outputs.ElectricStorage.degradation.annual_cycles}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Maintenance Cost</Typography>
                                        <Typography variant="body1">${optimization.results.outputs.ElectricStorage.degradation.maintenance_cost?.toLocaleString()}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Not simulated or no battery.</Typography>
                        )}
                    </SectionCard>
                </>
            )}

            <EmissionsPanel data={optimization.results?.outputs} />

            {scenarioForm.offGrid && (
                <>
                    <ResiliencePanel data={optimization.results?.outputs} />
                    <UnmetLoadTable results={optimization.results} isOffGrid={true} />
                </>
            )}
        </>
    );
}

/**
 * ResultsSection Component
 *
 * Main component for Tab 6 (Results) content with sub-tabs.
 */
function ResultsSection({
    isLoadingScenario,
    optimization,
    scenarioForm,
    loadProfile,
    financialAnalysis,
    capexSource,
    monthlyPerformanceData,
    optimizationEngine,
    vortxStyles,
    graphData,
    tph,
    isDark,
    budget,
    systemSizes,
    selectedEquipment,
    // Power flow state
    selectedDate,
    setSelectedDate,
    dayIndex,
    setDayIndex,
    showResilientMode,
    setShowResilientMode,
    outageStartHour,
    setOutageStartHour,
    // Sub-tab state
    subTab = 0,
    setSubTab,
    // Guarantee simulation props
    projectId,
    scenarioId,
}) {
    // Use internal state if no external state provided
    const [internalSubTab, setInternalSubTab] = useState(subTab);
    const resultsSubTab = setSubTab ? subTab : internalSubTab;
    const setResultsSubTab = setSubTab || setInternalSubTab;

    // Auth for VPP Revenue tab
    const { currentUser } = useAuth();

    // VPP estimate state — lifted from VPPRevenueTab for Deal Analysis integration
    const [vppEstimate, setVppEstimate] = useState(null);
    const handleVppEstimateChange = useCallback((data) => {
        setVppEstimate(data);
    }, []);

    // Check if CA DER charges apply
    const hasCaDerCharges = monthlyPerformanceData?.monthly_der_charges?.some(
        m => (m.total_der_charges || 0) > 0
    ) || monthlyPerformanceData?.ca_der_charges?.total_annual_ca_der_charges > 0
      || monthlyPerformanceData?.ca_der_charges?.standby_charges?.suspension_applied;

    // Tab indices: Performance(0), Deal Analysis(1), VPP Revenue(2), [CA DER(3)], Export(last)
    const getVppTabIndex = () => 2;
    const getCaDerTabIndex = () => 3;
    const getExportTabIndex = () => hasCaDerCharges ? 4 : 3;

    // Construct a project-like object for VPPRevenueTab from scenario data
    const vppProject = React.useMemo(() => {
        const batteryKw = optimization?.results?.outputs?.ElectricStorage?.size_kw || 0;
        const batteryKwh = optimization?.results?.outputs?.ElectricStorage?.size_kwh || 0;
        const annualSavings = financialAnalysis?.projectCashflows?.[0]?.energySavings
            || optimization?.results?.outputs?.Financial?.year_one_savings
            || 0;
        // Utility from tariff data — needed for DR program filtering
        const utilityName = scenarioForm.customTariffData?.utility_name
            || scenarioForm.selectedTariffData?.utility_name
            || scenarioForm.utilityName
            || '';

        return {
            location: {
                lat: scenarioForm.tariffLocation?.lat,
                lon: scenarioForm.tariffLocation?.lon,
            },
            utility: utilityName,
            apps: {
                site_dna: {
                    battery_kw: batteryKw,
                    battery_kwh: batteryKwh,
                },
            },
            financial_results: {
                annual_savings: annualSavings,
            },
        };
    }, [scenarioForm.tariffLocation, scenarioForm.customTariffData, scenarioForm.selectedTariffData, scenarioForm.utilityName, optimization?.results, financialAnalysis]);

    // Loading state
    if (isLoadingScenario) {
        return <LoadingCard title="Loading Scenario" message="Loading saved results..." />;
    }

    // Optimization in progress
    const isOptimizing = optimization.isSubmitting ||
        (optimization.runUuid && !optimization.results && optimization.status &&
         !optimization.status.includes('Error') && !optimization.status.includes('Failed'));

    if (isOptimizing) {
        return <OptimizationInProgressCard status={optimization.status} runUuid={optimization.runUuid} />;
    }

    // Has results
    if (optimization.results) {
        return (
            <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={resultsSubTab}
                        onChange={(e, newValue) => setResultsSubTab(newValue)}
                        sx={{
                            '& .MuiTab-root': { color: 'text.primary' },
                            '& .Mui-selected': { color: vortxColors.gold },
                            '& .MuiTabs-indicator': { backgroundColor: vortxColors.gold },
                        }}
                    >
                        <Tab icon={<ShowChart />} label="Performance" iconPosition="start" />
                        <Tab icon={<Analytics />} label="Deal Analysis" iconPosition="start" />
                        <Tab icon={<MonetizationOn />} label="VPP Revenue" iconPosition="start" />
                        {hasCaDerCharges && (
                            <Tab icon={<ElectricBolt />} label="CA DER Charges" iconPosition="start" />
                        )}
                        <Tab icon={<FileDownload />} label="Export" iconPosition="start" />
                    </Tabs>
                </Box>

                {/* Performance Sub-tab */}
                {resultsSubTab === 0 && (
                    <PerformanceSubTab
                        optimization={optimization}
                        scenarioForm={scenarioForm}
                        loadProfile={loadProfile}
                        financialAnalysis={financialAnalysis}
                        capexSource={capexSource}
                        monthlyPerformanceData={monthlyPerformanceData}
                        optimizationEngine={optimizationEngine}
                        vortxStyles={vortxStyles}
                        graphData={graphData}
                        tph={tph}
                        isDark={isDark}
                        vppEstimate={vppEstimate}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        dayIndex={dayIndex}
                        setDayIndex={setDayIndex}
                        showResilientMode={showResilientMode}
                        setShowResilientMode={setShowResilientMode}
                        outageStartHour={outageStartHour}
                        setOutageStartHour={setOutageStartHour}
                    />
                )}

                {/* Deal Analysis Sub-tab */}
                {resultsSubTab === 1 && (
                    <DealAnalysisTab
                        budget={budget}
                        systemSizes={systemSizes}
                        annualProductionKwh={optimization?.results?.outputs?.PV?.annual_energy_produced_kwh || 0}
                        annualSavings={financialAnalysis?.projectCashflows?.[0]?.energySavings || optimization?.results?.outputs?.Financial?.year_one_savings || 0}
                        bauUtilityRate={financialAnalysis?.summary?.bauUtilityRate || 0}
                        operatingCosts={budget?.opexCalculations || {}}
                        onConfigChange={(dealAnalysisState) => {
                            scenarioForm.setDealAnalysisConfig(dealAnalysisState);
                        }}
                        initialConfig={scenarioForm.dealAnalysisConfig}
                        monthlyPerformanceData={monthlyPerformanceData}
                        financialAnalysis={financialAnalysis}
                        vppEstimate={vppEstimate}
                        projectId={projectId}
                        scenarioId={scenarioId}
                        optimization={optimization}
                        budgetTabProps={{
                            generators: scenarioForm.generators,
                            selectedSolarData: selectedEquipment.selectedSolarData,
                            setSelectedSolarData: selectedEquipment.setSelectedSolarData,
                            selectedBatteryData: selectedEquipment.selectedBatteryData,
                            setSelectedBatteryData: selectedEquipment.setSelectedBatteryData,
                            selectedGeneratorData: selectedEquipment.selectedGeneratorData,
                            setSelectedGeneratorData: selectedEquipment.setSelectedGeneratorData,
                            selectedSwitchgearData: selectedEquipment.selectedSwitchgearData,
                            setSelectedSwitchgearData: selectedEquipment.setSelectedSwitchgearData,
                        }}
                    />
                )}

                {/* VPP Revenue Sub-tab — always mounted so estimate data flows to Deal Analysis */}
                <Box sx={{ display: resultsSubTab === getVppTabIndex() ? 'block' : 'none' }}>
                    <VPPRevenueTab project={vppProject} currentUser={currentUser} onEstimateChange={handleVppEstimateChange} />
                </Box>

                {/* CA DER Charges Sub-tab (conditional) */}
                {hasCaDerCharges && resultsSubTab === getCaDerTabIndex() && (
                    <CADERChargesAnalysis
                        monthlyPerformanceData={monthlyPerformanceData}
                        caDerConfig={scenarioForm.caDerConfig}
                    />
                )}

                {/* Export Sub-tab */}
                {resultsSubTab === getExportTabIndex() && (
                    <ExportDataTab
                        results={optimization.results}
                        loadProfile={loadProfile}
                        scenarioForm={scenarioForm}
                        scenarioName={scenarioForm.scenarioName || 'scenario'}
                        financialAnalysis={financialAnalysis}
                        budget={budget}
                    />
                )}
            </>
        );
    }

    // No results
    return (
        <NoResultsCard
            onNavigateToDesign={() => scenarioForm.setTab(1)}
            vortxStyles={vortxStyles}
        />
    );
}

export default ResultsSection;
